import React, { useEffect, useRef } from "react"
import QRCodeStyling from "qr-code-styling"
import { Typography } from "@mui/material"
import dayjs from "dayjs"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/es"
import { capitalizeNombresPropios } from "../../utils/StringUtils"
import { QrConfig, QrData } from "./qrConfigs"

dayjs.locale("es")
dayjs.extend(localizedFormat)

interface GenericQRCardProps {
  config: QrConfig
  data: QrData
  size?: number
}

const LABEL_TARJETAS: Record<string, string> = {
  tarjetasPagadas: "Tarjetas disponibles",
  tarjetasPagadasMenores: "Tarjetas de Menores disponibles",
  tarjetasBaile: "Tarjetas de Baile disponibles",
}

const GenericQRCard: React.FC<GenericQRCardProps> = ({ config, data, size }) => {
  const qrRef = useRef<HTMLDivElement>(null)
  const qrInstance = useRef<QRCodeStyling | null>(null)

  const qrValue = config.suffix ? `${data.qrBase}${config.suffix}` : data.qrBase
  const resolvedSize = size ?? config.size

  const cantidadTarjetas =
    config.campoTarjetas === "tarjetasPagadasMenores"
      ? data.tarjetasPagadasMenores
      : data.tarjetasPagadas

  const formattedDate = dayjs(data.fechaEvento).format("dddd, D [de] MMMM [de] YYYY")

  useEffect(() => {
    if (!qrRef.current) return

    qrRef.current.innerHTML = ""

    qrInstance.current = new QRCodeStyling({
      width: resolvedSize,
      height: resolvedSize,
      data: qrValue,
      type: config.drawType ?? "svg",
      shape: config.shape,
      margin: config.margin ?? 0,
      ...(config.image ? { image: config.image } : {}),
      imageOptions: { hideBackgroundDots: true, imageSize: 0.4, margin: 0, ...config.imageOptions },
      qrOptions: config.qrOptions,
      dotsOptions: config.dotsOptions,
      cornersSquareOptions: config.cornersSquareOptions,
      cornersDotOptions: config.cornersDotOptions,
      backgroundOptions: config.backgroundOptions,
    })

    qrInstance.current.append(qrRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrValue])

  return (
    <div className="border border-2 p-2 rounded mb-3">
      <Typography
        variant="h6"
        component="h4"
        textAlign="center"
        className="mb-4"
      >
        <strong>
          {formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}
        </strong>
      </Typography>
      <div className="qr-code-wrapper">
        <div ref={qrRef} />
      </div>
      <h5 className="mt-3 text-success">{config.titulo}:</h5>
      <div className="d-flex flex-column align-items-start ms-5">
        <p className="fs-6 mb-1">
          <strong>Nombre:</strong>{" "}
          {capitalizeNombresPropios(data.nombre)}{" "}
          {capitalizeNombresPropios(data.apellido)}
        </p>
        {data.dni && data.tipoEvento === "EGRESADOS" && (
          <p className="fs-6 mb-1">
            <strong>DNI:</strong> {data.dni}
          </p>
        )}
        <p className="fs-6 mb-1">
          <strong>{LABEL_TARJETAS[config.campoTarjetas]}:</strong>{" "}
          {cantidadTarjetas}
        </p>
      </div>
    </div>
  )
}

export default GenericQRCard
